import axios from 'axios';

import { signOut } from './auth';

// In production: empty base (same origin, FastAPI serves both)
// In local dev: use localhost:8001
// Exported because the agent event stream is read with fetch() rather than this axios
// instance — an EventSource cannot set an Authorization header — and it has to resolve
// against the same base, or the transcript alone breaks wherever the API is a different
// origin from the app.
export const API_BASE = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Multi-tenant: attach the active organization for org-scoped routes.
  const orgId = localStorage.getItem('active_org_id');
  if (orgId) {
    config.headers['X-Org-Id'] = orgId;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      signOut();
    }
    return Promise.reject(error);
  }
);

// Public VS Code extension download metadata (no auth) — for the landing page download link
export const getVscodeDownloadInfo = () => api.get('/api/download/vscode');

// Auth
export const login = (email, password) =>
  api.post('/api/admin/login', { email, password });

// Self-serve signup (public release) — a plain account, no org required
export const signup = (email, name, password) =>
  api.post('/api/auth/signup', { email, name, password });

// Platform feature flags (unauthenticated) — e.g. { allow_org_creation }
export const getAppConfig = () => api.get('/api/app-config');

// Organizations
export const createOrg = (name, visibility = 'private', join_policy = 'approval') =>
  api.post('/api/orgs', { name, visibility, join_policy });
export const getMyOrgs = () => api.get('/api/orgs/mine');
export const getOrg = (orgId) => api.get(`/api/orgs/${orgId}`);
export const updateOrg = (orgId, data) => api.patch(`/api/orgs/${orgId}`, data);
export const deleteOrg = (orgId) => api.delete(`/api/orgs/${orgId}`);
export const discoverOrgs = (q = '') => api.get('/api/orgs/discover', { params: { q } });
export const requestToJoin = (orgId, message = '') =>
  api.post(`/api/orgs/${orgId}/join-requests`, { message });
export const getJoinRequests = (orgId) => api.get(`/api/orgs/${orgId}/join-requests`);
export const approveJoinRequest = (orgId, rid) =>
  api.post(`/api/orgs/${orgId}/join-requests/${rid}/approve`);
export const rejectJoinRequest = (orgId, rid) =>
  api.post(`/api/orgs/${orgId}/join-requests/${rid}/reject`);
export const getOrgMembers = (orgId, page = 1, limit = 50, search = '') =>
  api.get(`/api/orgs/${orgId}/members`, { params: { page, limit, search: search || undefined } });
export const updateOrgMember = (orgId, userId, role) =>
  api.patch(`/api/orgs/${orgId}/members/${userId}`, { role });
export const leaveOrg = (orgId) => api.post(`/api/orgs/${orgId}/leave`);
export const transferOrgOwnership = (orgId, userId) =>
  api.post(`/api/orgs/${orgId}/transfer-ownership`, { user_id: userId });
export const removeOrgMember = (orgId, userId) =>
  api.delete(`/api/orgs/${orgId}/members/${userId}`);
// Depleting credit balance — distinct from a tier's rolling-window budget_limits.
export const setOrgCredits = (orgId, data) => api.patch(`/api/orgs/${orgId}/credits`, data);
export const resetOrgCredits = (orgId) => api.post(`/api/orgs/${orgId}/credits/reset`);
export const searchOrgUsers = (orgId, q) =>
  api.get(`/api/orgs/${orgId}/users/search`, { params: { q } });
export const createOrgInvite = (orgId, email, role = 'member') =>
  api.post(`/api/orgs/${orgId}/invites`, { email, role });
export const getOrgInvites = (orgId) => api.get(`/api/orgs/${orgId}/invites`);
export const revokeOrgInvite = (orgId, email) =>
  api.post(`/api/orgs/${orgId}/invites/revoke`, { email });
export const getInvitePreview = (token) => api.get(`/api/invites/${token}`);
export const acceptInvite = (token) => api.post('/api/invites/accept', { token });
export const declineInvite = (token) => api.post('/api/invites/decline', { token });

// Org-scoped API keys (key → org; used by LLM clients, no X-Org-Id needed)
export const createOrgKey = (orgId, name = 'default') => api.post(`/api/orgs/${orgId}/keys`, { name });
export const getOrgKeys = (orgId) => api.get(`/api/orgs/${orgId}/keys`);
export const revokeOrgKey = (orgId, keyId) => api.delete(`/api/orgs/${orgId}/keys/${keyId}`);

// Notifications
export const getNotifications = () => api.get('/api/notifications');
export const markNotificationRead = (id) => api.post(`/api/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.post('/api/notifications/read-all');

// Public auth (no token required) — accounts are created by an admin
export const forgotPassword = (email) =>
  api.post('/api/auth/forgot-password', { email });
export const resetPassword = (email, otp, password) =>
  api.post('/api/auth/reset-password', { email, otp, password });

// Self-service (logged-in user)
export const getMe = () => api.get('/api/admin/me');
export const regenerateMyKey = () => api.post('/api/admin/me/regenerate-key');
export const getMyUsage = (days = null, startDate = null, endDate = null, model = null, product = null, teamId = null) =>
  api.get('/api/admin/me/usage', {
    params: {
      days: days || undefined, start_date: startDate || undefined, end_date: endDate || undefined,
      model: model || undefined, product: product || undefined, team_id: teamId || undefined,
    },
  });
export const getUserUsageAdmin = (userId, days = null, teamId = null) =>
  api.get(`/api/admin/users/${userId}/usage`, { params: { days: days || undefined, team_id: teamId || undefined } });
export const emailMyUsageReport = (days = null, startDate = null, endDate = null) =>
  api.post('/api/admin/me/usage/email-report', null, {
    params: { days: days || undefined, start_date: startDate || undefined, end_date: endDate || undefined },
  });
export const changeMyPassword = (current_password, new_password) =>
  api.post('/api/admin/me/change-password', { current_password, new_password });
export const updateMyProfile = (data) => api.post('/api/admin/me/profile', data);
export const getMyBudgetAlerts = () => api.get('/api/admin/me/budget-alerts');
export const setMyBudgetAlerts = (enabled, thresholds) => api.put('/api/admin/me/budget-alerts', { enabled, thresholds });
// Org-scoped feedback — a member submits; the org's managers read their org's inbox.
export const submitFeedback = (orgId, { subject = '', message, category = null }) =>
  api.post(`/api/orgs/${orgId}/feedback`, { subject, message, category });
export const getOrgFeedback = (orgId) => api.get(`/api/orgs/${orgId}/feedback`);

// Shared Session Registry — org-scoped sharing of latentcode sessions.
// Push/resolve (`POST /sessions`, `GET /sessions/{id}`) and the plain
// `GET /sessions` list are latentcode-only (gateway API key) — mirrors
// /api/models vs the admin console, see routes/registry.py. The browser
// only ever calls the JWT-gated routes below.
export const getMySharedSessions = () => api.get('/api/registry/me/sessions');
export const listAdminSharedSessions = (search = '', page = 1, limit = 100) =>
  api.get('/api/registry/admin/sessions', { params: { search: search || undefined, page, limit } });
export const downloadSharedSessionFull = (regId) => api.get(`/api/registry/sessions/${regId}/full`);
export const hideSharedSession = (regId, hidden) => api.patch(`/api/registry/sessions/${regId}`, { hidden });
export const deleteSharedSession = (regId) => api.delete(`/api/registry/sessions/${regId}`);

// Per-user agent settings (GitHub token, model)
export const getAgentSettings = () => api.get('/api/agent-settings');
export const updateAgentSettings = (data) => api.put('/api/agent-settings', data);
export const getAgentRepos = () => api.get('/api/agent-settings/repos');
export const runAgentJob = (prompt, model) => api.post('/api/agent-settings/run-job', { prompt, model });
export const getAgentJobStatus = (jobId) => api.get(`/api/agent-settings/jobs/${jobId}`);
export const getAgentJobs = (limit = 20, skip = 0) => api.get('/api/agent-settings/jobs', { params: { limit, skip } });

// The org's GitHub App connection (per org; X-Org-Id is added above)
export const getGitHubApp = () => api.get('/api/github-app');
export const getGitHubAppConnectUrls = () => api.get('/api/github-app/connect-urls');
export const linkGitHubApp = (code, state) => api.post('/api/github-app/link', { code, state });
export const setGitHubAppAllowed = (repos) => api.put('/api/github-app/allowed', { repos });
export const disconnectGitHubApp = () => api.delete('/api/github-app');

// Tiers
export const getTiers = () => api.get('/api/admin/tiers');
export const createTier = (data) => api.post('/api/admin/tiers', data);
export const updateTier = (id, data) => api.put(`/api/admin/tiers/${id}`, data);
export const setDefaultTier = (id) => api.post(`/api/admin/tiers/${id}/set-default`);
export const deleteTier = (id) => api.delete(`/api/admin/tiers/${id}`);
export const setUserTier = (userId, tierId) => api.put(`/api/admin/users/${userId}/tier`, { tier_id: tierId });
// Per-member credit sub-cap — scoped to one org's pool (org_members), not a
// global field, so it can never leak into that person's standing in any
// other organization they're also a member of.
export const setMemberCredits = (orgId, userId, creditLimit) =>
  api.patch(`/api/orgs/${orgId}/members/${userId}/credits`, { credit_limit: creditLimit });
export const resetMemberCredits = (orgId, userId) => api.post(`/api/orgs/${orgId}/members/${userId}/credits/reset`);
// One write for many members — used by "select all" bulk actions so a
// roster of hundreds/thousands doesn't mean one request per member.
export const bulkSetMemberCredits = (orgId, userIds, creditLimit) =>
  api.patch(`/api/orgs/${orgId}/members/credits/bulk`, { user_ids: userIds, credit_limit: creditLimit });
// Every user_id matching the current search/filters, unpaginated — for
// "select all N matching" instead of just the loaded page.
export const getAllUserIds = (search = '', filters = {}) =>
  api.get('/api/admin/users/ids', {
    params: {
      search: search || undefined,
      tier_id: filters.tier_id || undefined,
      is_admin: filters.is_admin ?? undefined,
    },
  });

// Credit requests — a member asks for more of their own sub-cap; an org admin approves/declines.
export const createCreditRequest = (orgId, { message = '' } = {}) =>
  api.post(`/api/orgs/${orgId}/credit-requests`, { message });
export const getMyCreditRequest = (orgId) => api.get(`/api/orgs/${orgId}/credit-requests/mine`);
export const getCreditRequests = (orgId) => api.get(`/api/orgs/${orgId}/credit-requests`);
export const approveCreditRequest = (orgId, rid, creditLimit) =>
  api.post(`/api/orgs/${orgId}/credit-requests/${rid}/approve`, { credit_limit: creditLimit });
export const declineCreditRequest = (orgId, rid) =>
  api.post(`/api/orgs/${orgId}/credit-requests/${rid}/decline`);
export const deleteUser = (userId) => api.delete(`/api/admin/users/${userId}`);
export const getAvailableModels = () => api.get('/v1/models');
// The models an API key may use, asked with that key alone, as an SDK would: no login
// token (an admin's lists the whole catalog) and no org header.
export const getKeyModels = (apiKey) =>
  axios.get(`${API_BASE}/v1/models`, { headers: { Authorization: `Bearer ${apiKey}` } });

// Projects (multi-member workspaces: tier + members)
export const getProjects = () => api.get('/api/admin/projects');
export const createProject = (data) => api.post('/api/admin/projects', data);
export const getProject = (id) => api.get(`/api/admin/projects/${id}`);
export const getProjectMembers = (id, page = 1, limit = 20, search = '') =>
  api.get(`/api/admin/projects/${id}/members`, { params: { page, limit, search: search || undefined } });
export const updateProject = (id, data) => api.put(`/api/admin/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/api/admin/projects/${id}`);
export const addProjectMember = (id, data) => api.post(`/api/admin/projects/${id}/members`, data);
export const updateProjectMember = (id, userId, data) => api.put(`/api/admin/projects/${id}/members/${userId}`, data);
export const removeProjectMember = (id, userId) => api.delete(`/api/admin/projects/${id}/members/${userId}`);
export const getMyProjects = () => api.get('/api/admin/me/projects');
export const getProjectUsage = (id) => api.get(`/api/admin/projects/${id}/usage`);

// Guardrails (regex L1)
export const getGuardrails = () => api.get('/api/admin/guardrails');
export const setGuardrailConfig = (enabled) => api.put('/api/admin/guardrails/config', { enabled });
export const createGuardrail = (data) => api.post('/api/admin/guardrails', data);
export const updateGuardrail = (id, data) => api.put(`/api/admin/guardrails/${id}`, data);
export const deleteGuardrail = (id) => api.delete(`/api/admin/guardrails/${id}`);

// Guardrails (LLM-based L2)
export const getLlmGuardrail = () => api.get('/api/admin/guardrails/llm');
export const setLlmGuardrailConfig = (data) => api.put('/api/admin/guardrails/llm/config', data);
export const addLlmGuardrailPolicy = (data) => api.post('/api/admin/guardrails/llm/policies', data);
export const deleteLlmGuardrailPolicy = (id) => api.delete(`/api/admin/guardrails/llm/policies/${id}`);
export const testLlmGuardrail = (data) => api.post('/api/admin/guardrails/llm/test', data);

// Guardrails (PII / Presidio L3)
export const getPiiGuardrail = () => api.get('/api/admin/guardrails/pii');
export const setPiiGuardrailConfig = (data) => api.put('/api/admin/guardrails/pii/config', data);
export const addPiiPattern = (data) => api.post('/api/admin/guardrails/pii/patterns', data);
export const deletePiiPattern = (id) => api.delete(`/api/admin/guardrails/pii/patterns/${id}`);
export const testPiiGuardrail = (data) => api.post('/api/admin/guardrails/pii/test', data);

// Prompt management (per-model system prompts)
export const getPrompts = () => api.get('/api/admin/prompts');
export const setPromptConfig = (data) => api.put('/api/admin/prompts/config', data);
export const setProductPrompt = (data) => api.put('/api/admin/prompts/product', data);
export const setModelPrompt = (data) => api.put('/api/admin/prompts/model', data);

// Users
export const getUsers = (page = 1, limit = 50, search = '', filters = {}) =>
  api.get('/api/admin/users', {
    params: {
      page,
      limit,
      search: search || undefined,
      tier_id: filters.tier_id || undefined,
      is_admin: filters.is_admin ?? undefined,
    },
  });

// LLM Stats
export const emailLLMStatsReport = (days = null, startDate = null, endDate = null) =>
  api.post('/api/admin/llm-stats/email-report', null, {
    params: { days: days || undefined, start_date: startDate || undefined, end_date: endDate || undefined },
  });

export const getLLMStats = (days = null, model = null, startDate = null, endDate = null, userId = null, product = null, projectId = null) =>
  api.get('/api/admin/llm-stats', {
    params: {
      days: days || undefined,
      model: model || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      user_id: userId || undefined,
      product: product || undefined,
      project_id: projectId || undefined,
    },
  });

export const getTopLLMUsers = (days = null, limit = 20, startDate = null, endDate = null, userId = null, product = null) =>
  api.get('/api/admin/llm-stats/top-users', {
    params: {
      days: days || undefined,
      limit,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      user_id: userId || undefined,
      product: product || undefined,
    },
  });

export const getLLMHeatmap = (days = null, model = null, startDate = null, endDate = null, userId = null, product = null, projectId = null) =>
  api.get('/api/admin/llm-stats/heatmap', {
    params: {
      days: days || undefined,
      model: model || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      user_id: userId || undefined,
      product: product || undefined,
      project_id: projectId || undefined,
    },
  });

export const getUsageFilters = () => api.get('/api/admin/llm-stats/filters');
export const getUsageBreakdown = (params = {}) =>
  api.get('/api/admin/llm-stats/usage', { params });

export const getDailyByProvider = (days = null, startDate = null, endDate = null, userId = null, product = null, projectId = null) =>
  api.get('/api/admin/llm-stats/daily-by-provider', {
    params: {
      days: days || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      user_id: userId || undefined,
      product: product || undefined,
      project_id: projectId || undefined,
    },
  });

export const getLatencyHistogram = (days = null, model = null, startDate = null, endDate = null, userId = null, product = null, projectId = null) =>
  api.get('/api/admin/llm-stats/latency-histogram', {
    params: {
      days: days || undefined,
      model: model || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      user_id: userId || undefined,
      product: product || undefined,
      project_id: projectId || undefined,
    },
  });

export const getTopUsersByIndexingCost = (days = null, limit = 10, emailQuery = '') =>
  api.get('/api/admin/users/top-by-indexing-cost', {
    params: {
      days: days || undefined,
      limit,
      email_query: emailQuery || undefined,
    },
  });

// Support & Feedback
export const getSupportStats = () => api.get('/api/admin/support/stats');
export const getSupportRequests = (page = 1, limit = 50, type = '', status = '', category = '', search = '') =>
  api.get('/api/admin/support', {
    params: {
      page,
      limit,
      type: type || undefined,
      status: status || undefined,
      category: category || undefined,
      search: search || undefined
    }
  });
export const getSupportRequest = (requestId) => api.get(`/api/admin/support/${requestId}`);
export const replyToSupport = (requestId, message) =>
  api.post(`/api/admin/support/${requestId}/reply`, { message });
export const closeSupport = (requestId, message = '') =>
  api.post(`/api/admin/support/${requestId}/close`, { message: message || null });
export const updateSupportStatus = (requestId, status, priority) =>
  api.put(`/api/admin/support/${requestId}/status`, {
    status: status || undefined,
    priority: priority || undefined
  });

// User provisioning (create / admin role / API key)
export const createUser = (data) => api.post('/api/admin/users', data);
export const startBulkUpload = (data) => api.post('/api/admin/users/bulk', data);
export const getBulkJobStatus = (jobId) => api.get(`/api/admin/users/bulk/${jobId}`);
export const makeAdmin = (userId) => api.post(`/api/admin/users/${userId}/make-admin`);
export const removeAdmin = (userId) => api.post(`/api/admin/users/${userId}/remove-admin`);
// Settings (LatentRouter UI-driven config — generic key/value)
export const getSettings = () => api.get('/api/admin/settings');

export const upsertSetting = (key, value, { valueType, category, description } = {}) =>
  api.put(`/api/admin/settings/${encodeURIComponent(key)}`, {
    value,
    value_type: valueType,
    category,
    description,
  });

export const deleteSetting = (key) =>
  api.delete(`/api/admin/settings/${encodeURIComponent(key)}`);

// A section save as changes: the rows it wrote and the names it removed. A row it does not name
// stays as stored, so a page loaded before someone else's save cannot undo it.
export const changeSection = (key, { rows, removed, category }) =>
  api.patch(`/api/admin/settings/${encodeURIComponent(key)}`, { rows, removed, category });

// Read a provider's own capability feed and diff it against our declarations.
// Returns a proposal; writing is a separate save the admin performs.
// What an unsaved reasoning declaration is, and what it would send. Renders
// nothing itself — the body comes from the router's own resolver.
export const previewReasoning = (payload) =>
  api.post('/api/admin/settings/models/reasoning-preview', payload);

// Calls the model at each declared level and reports what happened. Spends real
// tokens, so it is never automatic.
export const testReasoning = (payload) =>
  api.post('/api/admin/settings/models/reasoning-test', payload);

// One live call of an audio model — transcription, speech or a live session. Spends real
// money, so it is never automatic.
export const testAudio = (payload) =>
  api.post('/api/admin/settings/models/audio-test', payload);

// What this model resolves to on each host, for the document on screen. Reads
// models.dev; calls no provider and spends nothing.
export const modelHosts = (payload) =>
  api.post('/api/admin/settings/models/hosts', payload);

// What every row of the unsaved models section resolves to. Reads models.dev; calls no provider.
export const resolveModels = (override) =>
  api.post('/api/admin/settings/models/resolved', { override });

export const refreshCatalog = () =>
  api.post('/api/admin/settings/models/catalog-refresh');

// Whether this install reads models.dev at all: a platform admin's to set. Read from the settings options.
export const setModelsDev = (enabled) =>
  api.put('/api/admin/settings/models/models-dev', { enabled });

// Harness Eval — benchmark coding-agent harnesses (LatentCode / Claude Code)
export const getEvalHarnesses = () => api.get('/api/admin/harness-eval/harnesses');
export const updateEvalHarness = (kind, data) => api.put(`/api/admin/harness-eval/harnesses/${kind}`, data);
export const probeEvalHarness = (kind) => api.post(`/api/admin/harness-eval/harnesses/${kind}/probe`);
export const getEvalTasks = () => api.get('/api/admin/harness-eval/tasks');
export const getEvalModels = () => api.get('/api/admin/harness-eval/models');
export const getEvalArtifacts = (runId) => api.get(`/api/admin/harness-eval/runs/${runId}/artifacts`);
export const getEvalArtifact = (runId, taskId, name) =>
  api.get(`/api/admin/harness-eval/runs/${runId}/artifacts/${taskId}`, { params: { name } });
// Fetched rather than linked: the endpoint needs the auth header, which a plain
// href cannot carry. responseType blob keeps the bytes intact.
export const downloadEvalArtifact = (runId, taskId, name) =>
  api.get(`/api/admin/harness-eval/runs/${runId}/artifacts/${taskId}/download`,
    { params: { name }, responseType: 'blob' });
// A name deletes just that file; without one, the task's whole set.
export const deleteEvalArtifacts = (runId, taskId = '', name = '') =>
  api.delete(`/api/admin/harness-eval/runs/${runId}/artifacts`,
    { params: { task_id: taskId, name } });
export const getEvalProviders = () => api.get('/api/admin/harness-eval/providers');
export const getEvalEnvironment = () => api.get('/api/admin/harness-eval/environment');
export const createEvalRun = (data) => api.post('/api/admin/harness-eval/runs', data);
export const getEvalRuns = (limit = 25, skip = 0) =>
  api.get('/api/admin/harness-eval/runs', { params: { limit, skip } });
export const getEvalRun = (runId) => api.get(`/api/admin/harness-eval/runs/${runId}`);
export const cancelEvalRun = (runId) => api.post(`/api/admin/harness-eval/runs/${runId}/cancel`);

// Public client config — gateway base URL clients should use (per-deployment).
export const getPublicConfig = () => api.get('/api/config');

// Rate-limit resets (admin) — clear consumed quota; limits are unchanged.
export const resetUserRateLimits = (userId) =>
  api.post(`/api/admin/users/${userId}/reset-rate-limits`);
export const resetProjectRateLimits = (projectId) =>
  api.post(`/api/admin/projects/${projectId}/reset-rate-limits`);
export const resetMemberRateLimits = (projectId, userId) =>
  api.post(`/api/admin/projects/${projectId}/members/${userId}/reset-rate-limits`);

export default api;
